import { SurveyType } from './type/survey.type';
import { SurveyInterface } from './survey.interface';
import { Question } from '../question';
import { filter, keys, difference, mapKeys, omit } from 'lodash';
import { map, distinctUntilChanged, startWith } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { SetOptions } from '@schoolbelle/common/object-change-tracker';
import { QuestionDataInterface } from '../question/base/questionData.interface';
import { SurveyDataInterface } from './base/surveyData.interface';
import { Subscription } from 'rxjs';

export class Survey extends SurveyType implements SurveyInterface {
  private clipboard: Question[] = [];
  private subscriptions: Subscription[] = [];
  constructor(survey_id: string = undefined, type:string = undefined) {
    super(survey_id, type);
    this.subscriptions.push(
      this.getChildTracker(['questions'])
        .onContentUpdate.pipe(
          startWith(null),
          map(() => keys(this.get('questions') || {})),
          distinctUntilChanged(
            (a: string[], b: string[]) => difference(a, b).length === 0
          )
        )
        .subscribe(() => this.refreshQuestions())
    );
  }

  getQuestions(conditions: any): Question[] {
    return filter(this.questions, conditions);
  }
  addQuestion(
    qData: Partial<QuestionDataInterface> = {},
    setOptions: SetOptions = undefined
  ): Question {
    let questions = this.questions;
    if (questions.length > 100) throw 'questions over 100 are not allowed';

    let question_id = qData.id || uuid();

    let question = (this._questions[question_id] = new Question(
      this,
      question_id,
      qData.type
    ));

    question.load({ ...qData, ...question.value }, { push: 'none' });
    this.getChildTracker('questions').notifyChangeThruTree([], setOptions);
    return question;
  }
  addSubQuestion(parentQuestion: Question): Question {
    let pos: number =
      parentQuestion.priority +
      1 +
      parentQuestion.getDecendantQuestions().length;
    // console.log(pos);
    const subQuestion = this.addQuestion({
      type: 'radio',
      priority: pos,
      dependency: { id: parentQuestion.id, conditions: [] }
    });
    return subQuestion;
  }
  deleteQuestions(conditions: any, setOptions: SetOptions = undefined) {
    let count = 0;
    this.getQuestions(conditions).forEach(question => {
      [question].concat(question.getDecendantQuestions()).forEach(q => {
        q.deleteOptions(undefined, { push: 'none' });
        let question_id = q.id,
          priority = q.priority;
        question.destroyTracker();
        delete this._questions[question_id];
        this.questions.filter(q => q.priority > priority).forEach(q => {
          q.set(['priority'], q.priority - 1, { push: 'none' });
        });
        this.set(['questions', question_id], null, { push: 'none' });
        count++;
      });
    });
    if (count)
      this.getChildTracker('questions').notifyChangeThruTree([], setOptions);
  }
  copyQuestion(question: Question, empty: boolean = true): Question[] {
    if (empty === true) this.clipboard.splice(0, this.clipboard.length);

    this.clipboard.push(question);
    this.getQuestions({ dependency: { id: question.id } }).forEach(
      childQuestion => {
        this.copyQuestion(childQuestion, false);
      }
    );
    return this.clipboard;
  }
  pasteQuestions(pos: number = this.questions.length): Question[] {
    if (this.clipboard.length === 0) throw 'Nothing to paste.';

    let copiedQuetions: Question[] = [];
    let questionIdMaps = {};
    let optionIdMaps = {};
    this.clipboard.forEach((question_to_copy, i) => {
      // copy question
      let q_data_to_copy = omit(
        { ...question_to_copy.value, priority: pos + i },
        ['options', 'id']
      );
      let copied_question = this.addQuestion(q_data_to_copy, { push: 'none' });
      copiedQuetions.push(copied_question);
      questionIdMaps[question_to_copy.id] = copied_question.id;

      // copy options
      question_to_copy.options.forEach((option_to_copy, i) => {
        let o_data_to_copy = omit(option_to_copy.value, ['id']);
        let copied_option = copied_question.addOption(o_data_to_copy, {
          push: 'none'
        });
        optionIdMaps[option_to_copy.id] = copied_option.id;
      });

      // dependency update
      if (i !== 0 && question_to_copy.dependency.id) {
        copied_question.dependency.id =
          questionIdMaps[copied_question.dependency.id];
        copied_question.dependency.conditions = copied_question.dependency.conditions.map(
          condition =>
            mapKeys(condition, (value, option_id) => optionIdMaps[option_id])
        );
      }
    });
    return copiedQuetions;
  }
  load(
    origin: Partial<SurveyDataInterface> = {},
    options: SetOptions = undefined
  ): void {
    origin = { ...origin, id: this.id };
    options = { push: 'down', ...options };
    super.load(origin, { push: 'none' });
    this.refreshQuestions();
    this.notifyChangeThruTree([], { push: 'down' });
  }

  private refreshQuestions() {
    let question_ids_new = keys(this.get(['questions'])),
      question_ids_old = keys(this._questions);
    difference(question_ids_new, question_ids_old).forEach(question_id => {
      this._questions[question_id] = new Question(
        this,
        question_id,
        this.get(['questions', question_id, 'type'])
      );
    });
    difference(question_ids_old, question_ids_new).forEach(question_id => {
      let question = this._questions[question_id];
      if (question) {
        question.destroyTracker();
        question.set([], null);
        delete this._questions[question_id];
      }
    });
  }
}
